import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@baaligames/shared-ui/lib/api';
import { useAuth } from '@baaligames/shared-ui/lib/auth';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import Icon from '@baaligames/shared-ui/components/Icon';

// Phase R5 / Task C (2026-05-10) — Verification Gate.
//
// This page is the post-login holding pen for users whose accounts
// exist + are not banned but whose email AND telegram are both
// unverified. Backend `isAccountUsable()` no longer blocks login on
// verification, so the user gets a JWT and a wallet ID — but
// `<VerifiedRoute>` immediately bounces them here, where they MUST
// complete one of two verification flows before the rest of the app
// becomes reachable.
//
// Two equal paths:
//   1. Email — they were already mailed a 24h verify link at signup;
//      "Resend email" re-issues the token and re-sends. They open
//      the link from their inbox; the existing /verify-email page
//      consumes the token. We poll /me every 4s; once
//      email_verified_at flips, we navigate to /.
//   2. Telegram — POST /auth/telegram-link returns a fresh deep link
//      to @BaaliGames_bot. They press Start in Telegram; the bot
//      sets telegram_verified_at via the existing webhook. Same poll
//      flips them through.
//
// "Sign out" is intentionally the only escape hatch — they cannot
// navigate to /, /games, /wallet, etc. via the URL bar (the
// VerifiedRoute wrapper catches it) and BottomNav is hidden for
// unverified users.

type TgLink = { deepLink: string; expiresInSec: number } | null;

export default function Verify() {
  const t = useT();
  const nav = useNavigate();
  const { user, refreshMe, logout } = useAuth();
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState<number | null>(null);
  const [resendErr, setResendErr] = useState<string | null>(null);
  const [tgLoading, setTgLoading] = useState(false);
  const [tgErr, setTgErr] = useState<string | null>(null);
  const [tg, setTg] = useState<TgLink>(null);
  const [polling, setPolling] = useState(true);
  const pollRef = useRef<number | null>(null);

  // Already verified? Bounce to home. Also catches the case where the
  // user lands here via an old bookmark after they've verified.
  useEffect(() => {
    if (!user) { nav('/login', { replace: true }); return; }
    if (user.email_verified_at || user.telegram_verified_at) {
      nav('/', { replace: true });
    }
  }, [user, nav]);

  // Poll /me every 15s while on this page so out-of-band verification
  // (email link clicked elsewhere, telegram /start) flips the user
  // through automatically. Stops on unmount.
  //
  // 2026-05-20 — 429-remediation. Was 4 s = 15 req/min from this one
  // page if the user lingered. 15 s = 4 req/min, still snappy enough
  // for an "I just clicked the email link" round-trip.
  useEffect(() => {
    if (!polling) return;
    pollRef.current = window.setInterval(() => {
      refreshMe();
    }, 15_000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [polling, refreshMe]);

  async function handleResend() {
    setResending(true); setResendErr(null);
    try {
      await api.post('/auth/verify-email/resend', {});
      setResentAt(Date.now());
    } catch (e) {
      setResendErr((e as ApiError).message);
    } finally { setResending(false); }
  }

  async function handleTelegram() {
    setTgLoading(true); setTgErr(null);
    try {
      // /auth/telegram-link is a public route (intentionally — pending
      // users without a session must also reach it from the post-
      // signup screen). It accepts an `identifier` in the body to
      // resolve the user. We pass the logged-in user's username
      // explicitly rather than refactor the route to optional-auth.
      const r = await api.post<{ deepLink: string; expiresInSec: number }>(
        '/auth/telegram-link', { identifier: user.username }
      );
      setTg(r);
      // Immediately open the deep link in a new tab — telegram://
      // links are picked up by the installed Telegram app on mobile,
      // and on desktop it opens t.me which prompts to open the app.
      try { window.open(r.deepLink, '_blank', 'noopener,noreferrer'); } catch { /* noop */ }
    } catch (e) {
      setTgErr((e as ApiError).message);
    } finally { setTgLoading(false); }
  }

  async function handleSignOut() {
    setPolling(false);
    await logout();
    nav('/login', { replace: true });
  }

  if (!user) return null;

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-head">
          <div className="auth-head-badge">
            <Icon name="mark_email_read" size={22} />
          </div>
          <h1 className="auth-h1">{t('verify.title')}</h1>
          <p className="auth-sub">{t('verify.subtitle')}</p>
        </div>

        {/* Email path */}
        <div className="rounded-xl border border-arena-200/70 dark:border-arena-600/40 bg-white/60 dark:bg-arena-800/40 p-3 mb-3">
          <div className="flex items-start gap-2">
            <Icon name="mail" className="text-arena-500 dark:text-neon-200/70 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-arena-700 dark:text-neon-300">
                {t('verify.tabEmail')}
              </div>
              <div className="text-xs text-arena-500 dark:text-neon-200/70 mt-0.5">
                {t('verify.emailSent', { email: user.email })}
              </div>
              <div className="text-[11px] text-arena-400 dark:text-neon-200/50 mt-0.5">
                {t('verify.emailHint')}
              </div>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="auth-cta-ghost inline-flex items-center gap-1 text-sm"
                >
                  {resending ? (
                    <>
                      <Icon name="progress_activity" className="animate-spin" />
                      {t('verify.gate.checking')}
                    </>
                  ) : (
                    <>
                      <Icon name="forward_to_inbox" />
                      {t('verify.gate.resend')}
                    </>
                  )}
                </button>
                {resentAt && !resending && (
                  <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">
                    {t('verify.gate.resent')}
                  </span>
                )}
                {resendErr && (
                  <div className="auth-error mt-2" role="alert">{resendErr}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Telegram path */}
        <div className="rounded-xl border border-arena-200/70 dark:border-arena-600/40 bg-white/60 dark:bg-arena-800/40 p-3 mb-3">
          <div className="flex items-start gap-2">
            <Icon name="bolt" className="text-arena-500 dark:text-neon-200/70 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-arena-700 dark:text-neon-300">
                {t('verify.tabTelegram')}
              </div>
              <div className="text-xs text-arena-500 dark:text-neon-200/70 mt-0.5">
                {t('verify.telegramExpiry')}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleTelegram}
                  disabled={tgLoading}
                  className="auth-cta inline-flex items-center gap-1 text-sm"
                >
                  {tgLoading ? (
                    <>
                      <Icon name="progress_activity" className="animate-spin" />
                      {t('verify.gate.checking')}
                    </>
                  ) : (
                    <>
                      <Icon name="open_in_new" />
                      {t('verify.telegramOpen')}
                    </>
                  )}
                </button>
                {tg && (
                  <a
                    href={tg.deepLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-neon-600 dark:text-neon-300 underline underline-offset-2"
                  >
                    {t('verify.gate.tgFallback')}
                  </a>
                )}
              </div>
              {tgErr && (
                <div className="auth-error mt-2" role="alert">{tgErr}</div>
              )}
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-arena-500 dark:text-neon-200/60 mt-2">
          <Icon name="progress_activity" className="animate-spin inline-block align-middle mr-1" />
          {t('verify.gate.waiting')}
        </div>

        <div className="auth-foot flex items-center justify-between">
          <span className="text-xs text-arena-500 dark:text-neon-200/60">
            {user.email}
          </span>
          <button
            type="button"
            onClick={handleSignOut}
            className="auth-link text-xs"
          >
            {t('verify.gate.signOut')} →
          </button>
        </div>
      </div>
    </div>
  );
}
